import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  viewChild
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'

import { HlmAlertDialogModule } from '@yoshu-lib/ui/ui-alertdialog-helm/src'
import { HlmButtonModule } from '@yoshu-lib/ui/ui-button-helm/src'
import { HlmCardModule } from '@yoshu-lib/ui/ui-card-helm/src'
import { HlmDialogModule } from '@yoshu-lib/ui/ui-dialog-helm/src'
import { HlmIconModule } from '@yoshu-lib/ui/ui-icon-helm/src'
import { HlmInputModule } from '@yoshu-lib/ui/ui-input-helm/src'
import { HlmLabelModule } from '@yoshu-lib/ui/ui-label-helm/src'
import { HlmSmallDirective } from '@yoshu-lib/ui/ui-typography-helm/src'

import { Protocol, ProtocolCreateRequest } from '@yoshu-lib/models/protocol.model'

import { NgIcon } from '@ng-icons/core'
import { BrnAlertDialogComponent, BrnAlertDialogModule } from '@spartan-ng/brain/alert-dialog'
import { BrnDialogComponent, BrnDialogModule } from '@spartan-ng/brain/dialog'

import { ProtocolsTableComponent } from './protocols-table/protocols-table.component'
import { ProtocolsService } from './protocols.service'

@Component({
  standalone: true,
  imports: [
    HlmButtonModule,
    NgIcon,
    HlmIconModule,
    HlmSmallDirective,
    HlmCardModule,
    ProtocolsTableComponent,
    BrnAlertDialogModule,
    HlmAlertDialogModule,
    BrnDialogModule,
    HlmDialogModule,
    HlmInputModule,
    HlmLabelModule,
    FormsModule
  ],
  selector: 'app-protocols',
  templateUrl: 'protocols.component.html',
  styles: `
    :host {
      @apply flex h-full flex-col gap-4 overflow-y-auto px-8 pb-6 pt-4;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProtocolsComponent implements OnInit {
  private readonly protocolsService = inject(ProtocolsService)
  private readonly router = inject(Router)

  protected readonly protocolList = signal<Protocol[]>([])
  protected readonly isLoading = signal(false)
  private readonly protocolId = signal<string | null>(null)

  // Form data for creating a new protocol
  protected newProtocol: ProtocolCreateRequest = {
    name: '',
    description: '',
    global_prompt: ''
  }

  // Dialog references
  private readonly alertDialog = viewChild.required('alertDialog', {
    read: BrnAlertDialogComponent
  })
  private readonly createDialog = viewChild.required('createDialog', { read: BrnDialogComponent })

  ngOnInit() {
    console.log('ProtocolsComponent initialized')
    this.getAllProtocols()
  }

  getAllProtocols() {
    this.isLoading.set(true)
    this.protocolsService.getProtocols().subscribe({
      next: (protocols) => {
        this.protocolList.set(protocols)
        this.isLoading.set(false)
      },
      error: () => {
        this.isLoading.set(false)
      }
    })
  }

  openCreateDialog() {
    // Reset form
    this.newProtocol = {
      name: '',
      description: '',
      global_prompt: ''
    }
    console.log('openCreateDialog called')
    console.log('createDialog:', this.createDialog())
    this.createDialog().open()
  }

  createProtocol(ctx: { close: () => void }) {
    console.log('createProtocol called', this.newProtocol)
    console.log('ctx:', ctx)

    if (!this.newProtocol.name || !this.newProtocol.description) {
      console.log('Validation failed: name or description is empty')
      return
    }

    console.log('Sending request to create protocol')
    this.isLoading.set(true)
    this.protocolsService.createProtocol(this.newProtocol).subscribe({
      next: (response) => {
        console.log('Protocol created successfully', response)
        ctx.close()
        this.getAllProtocols()
      },
      error: (error) => {
        console.error('Error creating protocol', error)
        this.isLoading.set(false)
      }
    })
  }

  openDeleteDialog(protocolId: string) {
    this.protocolId.set(protocolId)
    this.alertDialog().open()
  }

  deleteProtocol(ctx: { close: () => void }) {
    const protocolId = this.protocolId()

    if (protocolId) {
      this.isLoading.set(true)

      this.protocolsService.deleteProtocol(protocolId).subscribe({
        next: () => {
          ctx.close()
          this.getAllProtocols()
        },
        error: () => {
          this.isLoading.set(false)
        }
      })
    }
  }

  viewProtocol(protocolId: string) {
    this.router.navigate(['/protocols', protocolId])
  }

  createProtocolAndCloseDialog(ctx: { close: () => void }) {
    console.log('createProtocolAndCloseDialog called', ctx)

    // Validate form
    if (!this.newProtocol.name || !this.newProtocol.description) {
      console.log('Validation failed: name or description is empty')
      return
    }

    console.log('Sending request to create protocol directly')
    this.isLoading.set(true)

    // Send request directly
    this.protocolsService.createProtocol(this.newProtocol).subscribe({
      next: (response) => {
        console.log('Protocol created successfully', response)

        // Close dialog manually
        try {
          if (ctx && typeof ctx.close === 'function') {
            ctx.close()
          } else {
            console.log('Using createDialog to close')
            this.createDialog().close()
          }
        } catch (error) {
          console.error('Error closing dialog', error)
        }

        this.getAllProtocols()
      },
      error: (error) => {
        console.error('Error creating protocol', error)
        this.isLoading.set(false)
      }
    })
  }
}
